import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Button from '@mui/material/Button';
import { grey } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { DataGrid, gridClasses } from '@mui/x-data-grid';

import { removeItemBi } from '../features/powerBiSlice';
import { deleteDashboardAPI } from '../services/deleteDashboard.service';
import { deleteReportAPI } from '../services/deleteReport.service';


function search(id, inputArray, field, proprety) {
  for (let i = 0; i < inputArray.length; i++) {
    if (inputArray[i][proprety] === id) {
      return inputArray[i][field];
    };
  }
}

export default function Table(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const skipNextPageChange = useRef(false);


  const handleRedirect = (id) => {
    navigate('/powerbi/' + id);
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;
    const { _id, id } = deleteTarget;
    dispatch(removeItemBi({ id: _id, type: props.type }));
    switch (props.type) {
      case 'dashboard':
        deleteDashboardAPI(id);
        break;
      case 'report':
        deleteReportAPI(id);
        break;
      default:
        break;
    }
    setDeleteTarget(null);
  };


  const dashboard = [
    {
      field: 'companyId',
      flex: 1,
      headerName: 'Nombre compañia',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return isNaN(params.row.companyId)
          ? params.row.companyId
          : search(
              params.row.companyId,
              props.ids.company,
              'nombreCompania',
              'id'
            );
      },
    },
    {
      field: 'reportName',
      flex: 1,
      headerName: 'Titulo Dashbord',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'isActive',
      flex: 1,
      headerName: 'Estado',
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return (
          <Switch
            checked={params.row.isActive}
            onChange={(event) => props.handleSwitch(event, params.row)}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 150,
      cellClassName: 'actions',
      getActions: (params) => {
        return [
          <IconButton onClick={() => props.handleEditItem(params.row)}>
            <EditIcon />
          </IconButton>,
          <IconButton
            onClick={() => setDeleteTarget({ _id: params.row._id, id: params.row.id })}
          >
            <DeleteIcon />
          </IconButton>,
          <IconButton onClick={() => handleRedirect(params.row.id)}>
            <VisibilityIcon />
          </IconButton>,
        ];
      },
    },
  ];

  const report = [
    {
      field: 'name',
      flex: 1,
      headerName: 'Nombre Reporte',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'descripcion',
      flex: 1,
      headerName: 'Descripción',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 100,
      cellClassName: 'actions',
      getActions: (params) => {
        return [
          <IconButton onClick={() => props.handleEditItem(params.row)}>
            <EditIcon />
          </IconButton>,
          <IconButton
            onClick={() => setDeleteTarget({ _id: params.row._id, id: params.row.id })}
          >
            <DeleteIcon />
          </IconButton>,
        ];
      },
    },
  ];

  const renderColumn = () => {
    switch (props.type) {
      case 'dashboard':
        return dashboard;
      case 'report':
        return report;
    }
  };

  const columns = useMemo(() => renderColumn(), [props.type]);

  useEffect(() => {
    setRows(props.tableData);
  }, [props.tableData]);

  const filteredRows = useMemo(() => {
    if (searchText.trim() === '') return rows;
    const lower = searchText.toLowerCase();
    return rows.filter((row) => {
      const nameField = props.type === 'report' ? row.name : row.reportName;
      return (nameField || '').toLowerCase().includes(lower);
    });
  }, [rows, searchText, props.type]);

  return (
    <>
      <TextField
        size="small"
        placeholder={`Buscar por ${props.type === 'report' ? 'nombre' : 'título'}...`}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">🔍</InputAdornment>
          ),
        }}
        sx={{ mb: 1, width: 300 }}
      />
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row._id}
        pagination
        rowsPerPageOptions={[5, 10, 20]}
        page={page}
        onPageChange={(newPage) => {
          if (skipNextPageChange.current) return;
          setPage(newPage);
        }}
        pageSize={pageSize}
        onPageSizeChange={(newSize) => {
          const firstRowIndex = page * pageSize;
          const newPage = Math.floor(firstRowIndex / newSize);
          const maxPage = Math.max(0, Math.ceil(filteredRows.length / newSize) - 1);
          const targetPage = Math.min(newPage, maxPage);
          skipNextPageChange.current = true;
          setPage(targetPage);
          setPageSize(newSize);
          setTimeout(() => { skipNextPageChange.current = false; }, 0);
        }}
        componentsProps={{ pagination: { showFirstButton: true, showLastButton: true } }}
        getRowSpacing={(params) => ({
          top: params.isFirstVisible ? 0 : 5,
          bottom: params.isLastVisible ? 0 : 5,
        })}
        sx={{
          [`& .${gridClasses.row}`]: {
            bgcolor: (theme) =>
              theme.palette.mode === 'light' ? grey[200] : grey[900],
          },
        }}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar este elemento? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
